import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, Plus, Send, Clock, Archive, ArchiveRestore, Trash2, Pencil, Users2, ExternalLink, Columns3, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const STATI = ["aperto","in_lavorazione","in_attesa","risolto","chiuso"];
const PRIORITA = ["bassa","normale","alta","urgente"];
const PRIO_COLOR = { bassa: "bg-slate-100 text-slate-700", normale: "bg-blue-100 text-blue-700", alta: "bg-amber-100 text-amber-800", urgente: "bg-rose-100 text-rose-800" };
const STATO_COLOR = { aperto: "bg-emerald-100 text-emerald-700", in_lavorazione: "bg-blue-100 text-blue-700", in_attesa: "bg-amber-100 text-amber-800", risolto: "bg-slate-200 text-slate-700", chiuso: "bg-slate-300 text-slate-500" };
const POST_COLORS = ["bg-yellow-50 border-yellow-300", "bg-emerald-50 border-emerald-300", "bg-blue-50 border-blue-300", "bg-rose-50 border-rose-300", "bg-purple-50 border-purple-300"];
const fmt = (n) => n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtEuro = (n) => n == null ? "—" : `€ ${fmt(n)}`;

export function TicketsBoard({ tipo, cantiereId, computoId }) {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSub = tipo === "subappaltatore";
  const isTec = tipo === "tecnico";
  const isSubUser = user?.role === "SUBAPPALTATORE";
  const isCantiereScope = !!cantiereId;
  const canEdit = user && ["ADMIN","TECNICO"].includes(user.role);
  const canCreate = canEdit || (isSubUser && isSub);
  const isAdmin = user?.role === "ADMIN";

  const [rows, setRows] = useState([]);
  const [view, setView] = useState((isSub || isTec) && isAdmin ? "grouped" : "all");
  const [filters, setFilters] = useState({ subtipo: "all", cantiere_id: "all", stato: "all", is_archived: false });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [cantieri, setCantieri] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [dips, setDips] = useState([]);
  const [expandedSub, setExpandedSub] = useState({});
  const _uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k_${Date.now()}_${Math.random().toString(36).slice(2)}`);
  const emptyLine = () => ({ _key: _uid(), dipendente_id: "", tipo_operaio: "", ore: "8", costo_orario: "35" });
  const emptyMatLine = () => ({ _key: _uid(), tipo_lavorazione: "", materiale_nome: "", quantita: "", unita_misura: "" });
  const emptyForm = { id: null, cantiere_id: "", riga_computo_id: "none", tecnico_id: "none", subappaltatore_id: "none", dipendente_id: "none",
    titolo: "", descrizione: "", priorita: "normale", scadenza: "",
    subtipo: isSub ? "manodopera" : "", data: "", tipo_operaio: "", num_operai: "1", ore: "", costo_orario: "",
    tipo_lavorazione: "", materiale_nome: "", unita_misura: "", quantita: "", lines: [emptyLine()], materiali_lines: [emptyMatLine()] };
  const [form, setForm] = useState(emptyForm);

  // Nel tab cantiere/gruppo per Ticket Subappaltatori mostra SEMPRE anche gli archiviati (storico).
  const historyMode = (isSub && !!groupId && !isSubUser) || (isSub && isCantiereScope);
  const load = async () => {
    const params = { tipo, group_id: groupId };
    if (isCantiereScope) params.cantiere_id = cantiereId;
    if (computoId) params.computo_id = computoId;
    if (historyMode) params.include_archived = true;
    else params.is_archived = filters.is_archived;
    if (filters.subtipo !== "all" && isSub) params.subtipo = filters.subtipo;
    if (!isCantiereScope && filters.cantiere_id !== "all") params.cantiere_id = filters.cantiere_id;
    if (filters.stato !== "all") params.stato = filters.stato;
    const { data } = await api.get("/tickets", { params });
    setRows(data);
  };
  useEffect(() => { load(); }, [tipo, groupId, cantiereId, computoId, filters.subtipo, filters.cantiere_id, filters.stato, filters.is_archived]);
  useEffect(() => {
    // SUB user: usa la lista dei cantieri associati (portale). Altri: usa il gruppo corrente.
    if (isSubUser) {
      api.get("/portale/cantieri").then(r => setCantieri(r.data || [])).catch(() => setCantieri([]));
    } else {
      api.get("/cantieri", { params: { group_id: groupId } }).then(r => setCantieri(r.data)).catch(() => {});
    }
  }, [groupId, isSubUser]);
  useEffect(() => { const kind = tipo === "tecnico" ? "tecnici" : "subappaltatori"; api.get(`/anagrafiche/${kind}`).then(r => setAssignees(r.data)).catch(() => setAssignees([])); }, [tipo]);
  useEffect(() => {
    if (isSub && form.subappaltatore_id && form.subappaltatore_id !== "none") {
      api.get("/dipendenti-subappaltatori", { params: { subappaltatore_id: form.subappaltatore_id } })
        .then(r => setDips(r.data || [])).catch(() => setDips([]));
    } else { setDips([]); }
  }, [isSub, form.subappaltatore_id]);

  const openNew = () => {
    setEditing(null);
    // Se SUB user: forza subappaltatore_id al proprio (nessuna scelta possibile).
    const forcedSub = isSubUser && user?.subappaltatore_id ? user.subappaltatore_id : "none";
    const forcedCant = isCantiereScope ? cantiereId : "";
    setForm({ ...emptyForm, cantiere_id: forcedCant, subappaltatore_id: forcedSub, data: new Date().toISOString().slice(0,10), lines: [emptyLine()], materiali_lines: [emptyMatLine()] });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    const existingLines = Array.isArray(t.lines) && t.lines.length
      ? t.lines.map(l => ({ dipendente_id: l.dipendente_id || "", tipo_operaio: l.tipo_operaio || "", ore: l.ore != null ? String(l.ore) : "", costo_orario: l.costo_orario != null ? String(l.costo_orario) : "" }))
      : [{ ...emptyLine }];
    setForm({
      id: t.id, cantiere_id: t.cantiere_id, riga_computo_id: t.riga_computo_id || "none",
      tecnico_id: t.tecnico_id || "none", subappaltatore_id: t.subappaltatore_id || "none",
      dipendente_id: t.dipendente_id || "none",
      titolo: t.titolo, descrizione: t.descrizione || "", priorita: t.priorita, scadenza: t.scadenza || "",
      subtipo: t.subtipo || "manodopera", data: t.data || "",
      tipo_operaio: t.tipo_operaio || "", num_operai: t.num_operai != null ? String(t.num_operai) : "1",
      ore: t.ore != null ? String(t.ore) : "", costo_orario: t.costo_orario != null ? String(t.costo_orario) : "",
      tipo_lavorazione: t.tipo_lavorazione || "", materiale_nome: t.materiale_nome || "",
      unita_misura: t.unita_misura || "", quantita: t.quantita != null ? String(t.quantita) : "",
      lines: existingLines,
    });
    setOpen(true);
  };

  const addLine = () => setForm({ ...form, lines: [...(form.lines || []), emptyLine()] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.length > 1 ? form.lines.filter((_, idx) => idx !== i) : form.lines });
  const updateLine = (i, patch) => setForm({ ...form, lines: form.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) });
  const onPickLineDip = (i, v) => {
    const dip = dips.find(d => d.id === v);
    updateLine(i, { dipendente_id: v, tipo_operaio: dip?.ruolo || form.lines[i].tipo_operaio || "" });
  };
  const usedLineDipIds = new Set((form.lines || []).map(l => l.dipendente_id).filter(Boolean));

  const save = async (e) => {
    e.preventDefault();
    if (!form.cantiere_id) return toast.error("Cantiere richiesto");
    if (isSub && (!form.subappaltatore_id || form.subappaltatore_id === "none")) return toast.error("Subappaltatore richiesto");
    const payload = { tipo, ...form };
    ["riga_computo_id","tecnico_id","subappaltatore_id","dipendente_id"].forEach(k => { if (payload[k] === "none") payload[k] = null; });
    if (!isSub && !isTec) { delete payload.subtipo; delete payload.data; delete payload.tipo_operaio; delete payload.num_operai; delete payload.ore; delete payload.costo_orario; delete payload.tipo_lavorazione; delete payload.materiale_nome; delete payload.unita_misura; delete payload.quantita; delete payload.lines; delete payload.materiali_lines; }
    else {
      if (!form.subtipo || !["manodopera","materiali"].includes(form.subtipo)) return toast.error("Scegli il tipo: Dipendenti o Materiali");
      payload.subtipo = form.subtipo;
      if (!editing) {
        if (form.subtipo === "manodopera") {
          const validLines = (form.lines || []).filter(l => l.dipendente_id && Number(l.ore) > 0);
          if (validLines.length === 0) return toast.error("Aggiungi almeno un dipendente con ore");
          if (new Set(validLines.map(l => l.dipendente_id)).size !== validLines.length) return toast.error("Dipendente ripetuto: usa una sola riga per persona");
          // Ticket Dipendenti: solo ore. Nessun costo economico.
          payload.lines = validLines.map(l => ({ dipendente_id: l.dipendente_id, tipo_operaio: l.tipo_operaio || null, ore: Number(l.ore), costo_orario: null }));
          payload.materiali_lines = [];
        } else {
          const validMat = (form.materiali_lines || []).filter(m => m.materiale_nome && m.materiale_nome.trim() !== "");
          if (validMat.length === 0) return toast.error("Aggiungi almeno un materiale");
          payload.materiali_lines = validMat.map(m => ({
            tipo_lavorazione: m.tipo_lavorazione || null,
            materiale_nome: m.materiale_nome.trim(),
            quantita: m.quantita !== "" ? Number(m.quantita) : null,
            unita_misura: m.unita_misura || null,
          }));
          payload.lines = [];
        }
      } else {
        delete payload.lines; delete payload.materiali_lines;
      }
      // Legacy campi single: azzerati (le righe sono la fonte di verità)
      payload.dipendente_id = null; payload.tipo_operaio = null; payload.num_operai = null;
      payload.ore = null; payload.costo_orario = null;
      payload.tipo_lavorazione = null; payload.materiale_nome = null; payload.unita_misura = null; payload.quantita = null;
    }
    if (!payload.scadenza) payload.scadenza = null;
    if (!payload.data) payload.data = null;
    delete payload.id;
    try {
      if (editing) await api.patch(`/tickets/${editing.id}`, payload);
      else await api.post("/tickets", payload);
      toast.success(editing ? "Ticket aggiornato" : "Ticket creato");
      setOpen(false); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const archive = async (id) => { try { await api.post(`/tickets/${id}/archive`); toast.success("Archiviato"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const restore = async (id) => { try { await api.post(`/tickets/${id}/restore`); toast.success("Ripristinato"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const del = async (id) => { if (!confirm("Eliminare DEFINITIVAMENTE questo ticket?")) return; try { await api.delete(`/tickets/${id}`); toast.success("Eliminato"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };

  const changeStato = async (id, newStato) => {
    const current = rows.find(r => r.id === id);
    if (!current || current.stato === newStato) return;
    const prev = rows;
    setRows(rs => rs.map(r => r.id === id ? { ...r, stato: newStato } : r));
    try {
      await api.patch(`/tickets/${id}`, { stato: newStato });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento stato");
      setRows(prev);
    }
  };

  const grouped = useMemo(() => {
    const m = {};
    for (const t of rows) {
      const key = isSub ? (t.subappaltatore_id || "__none__") : (t.tecnico_id || "__none__");
      const idField = isSub ? t.subappaltatore_id : t.tecnico_id;
      const nome = assignees.find(a => a.id === idField)?.nome || (idField ? "—" : `(senza ${isSub ? "subappaltatore" : "tecnico"})`);
      if (!m[key]) m[key] = { id: key, nome, tickets: [] };
      m[key].tickets.push(t);
    }
    return Object.values(m).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [rows, assignees, isSub]);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div><p className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Bacheca collaborativa</p><h1 className="text-3xl font-extrabold tracking-tight mt-1">Ticket {isSub ? "subappaltatori" : "tecnici"}</h1></div>
        {canCreate && <Button onClick={openNew} data-testid={`board-new-${tipo}`} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"><Plus className="h-4 w-4 mr-2" /> Nuovo ticket</Button>}
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {(isSub || (isTec && isAdmin)) && (
          <button onClick={() => setView("grouped")} data-testid="view-grouped" className={`px-3 py-1.5 text-xs rounded-full border ${view === "grouped" ? "bg-amber-500 text-slate-950 border-amber-500 font-semibold" : "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700"}`}><Users2 className="h-3 w-3 inline mr-1" />Per {isSub ? "subappaltatore" : "tecnico"}</button>
        )}
        <button onClick={() => setView("all")} data-testid="view-all" className={`px-3 py-1.5 text-xs rounded-full border ${view === "all" ? "bg-amber-500 text-slate-950 border-amber-500 font-semibold" : "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700"}`}>Tutti</button>
        <button onClick={() => setView("kanban")} data-testid="view-kanban" className={`px-3 py-1.5 text-xs rounded-full border ${view === "kanban" ? "bg-amber-500 text-slate-950 border-amber-500 font-semibold" : "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700"}`}><Columns3 className="h-3 w-3 inline mr-1" />Kanban</button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-end">
        {isSub && (
          <div className="w-40"><Label className="text-[10px]">Subtipo</Label>
            <Select value={filters.subtipo} onValueChange={(v) => setFilters({ ...filters, subtipo: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="filter-subtipo"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="manodopera">Manodopera</SelectItem><SelectItem value="materiali">Materiali</SelectItem></SelectContent>
            </Select>
          </div>
        )}
        {!isCantiereScope && (
        <div className="w-44"><Label className="text-[10px]">Cantiere</Label>
          <Select value={filters.cantiere_id} onValueChange={(v) => setFilters({ ...filters, cantiere_id: v })}>
            <SelectTrigger className="h-8 text-xs" data-testid="filter-cantiere"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti</SelectItem>{cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.code}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        )}
        <div className="w-36"><Label className="text-[10px]">Stato</Label>
          <Select value={filters.stato} onValueChange={(v) => setFilters({ ...filters, stato: v })}>
            <SelectTrigger className="h-8 text-xs" data-testid="filter-stato"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti</SelectItem>{STATI.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 p-0.5">
          <button onClick={() => setFilters({ ...filters, is_archived: false })} data-testid="filter-attivi" className={`px-3 py-1 text-xs font-semibold rounded ${!filters.is_archived ? "bg-white dark:bg-slate-900 text-amber-600 shadow-sm" : "text-slate-500"} ${historyMode ? "hidden" : ""}`}>Attivi</button>
          <button onClick={() => setFilters({ ...filters, is_archived: true })} data-testid="filter-archivio" className={`px-3 py-1 text-xs font-semibold rounded ${filters.is_archived ? "bg-white dark:bg-slate-900 text-amber-600 shadow-sm" : "text-slate-500"} ${historyMode ? "hidden" : ""}`}><Archive className="h-3 w-3 inline mr-1" />Archivio</button>
          {historyMode && <span className="px-3 py-1 text-xs font-semibold text-amber-600" data-testid="history-mode-label">Storico completo (attivi + archiviati)</span>}
        </div>
      </div>

      {rows.length === 0 ? <Card><CardContent className="p-10 text-center text-slate-500">Nessun ticket con questi filtri</CardContent></Card> : (
        view === "kanban" ? (
          <KanbanView rows={rows} canDrag={canEdit && !filters.is_archived} onStatoChange={changeStato} cardProps={{ isAdmin, archived: filters.is_archived, onOpen: (t) => setSelected(t.id), onEdit: (t) => openEdit(t), onArchive: (t) => archive(t.id), onRestore: (t) => restore(t.id), onDelete: (t) => del(t.id), onGoto: (t) => t.group_id && t.cantiere_id && t.riga_computo_id && navigate(`/g/${t.group_id}/cantieri/${t.cantiere_id}/righe/${t.riga_computo_id}?highlight=riga`) }} />
        ) :
        isSub && view === "grouped" ? (
          <div className="space-y-3">{grouped.map(g => (
            <Card key={g.id} data-testid={`grouped-${g.id}`}><CardContent className="p-4">
              <button onClick={() => setExpandedSub({ ...expandedSub, [g.id]: !expandedSub[g.id] })} className="w-full flex items-center justify-between mb-2" data-testid={`grouped-toggle-${g.id}`}>
                <div className="flex items-center gap-2"><Users2 className="h-4 w-4 text-amber-600" /><h3 className="font-semibold">{g.nome}</h3></div>
                <Badge variant="outline">{g.tickets.length} ticket</Badge>
              </button>
              {expandedSub[g.id] !== false && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
                  {g.tickets.map((t, i) => <TicketCard key={t.id} t={t} i={i} isAdmin={isAdmin} onOpen={() => setSelected(t.id)} onEdit={() => openEdit(t)} onArchive={() => archive(t.id)} onRestore={() => restore(t.id)} onDelete={() => del(t.id)} onGoto={() => t.group_id && t.cantiere_id && t.riga_computo_id && navigate(`/g/${t.group_id}/cantieri/${t.cantiere_id}/righe/${t.riga_computo_id}?highlight=riga`)} archived={filters.is_archived} />)}
                </div>
              )}
            </CardContent></Card>
          ))}</div>
        ) : isTec && view === "grouped" && isAdmin ? (
          <div className="space-y-3">{grouped.map(g => (
            <Card key={g.id} data-testid={`grouped-tec-${g.id}`}><CardContent className="p-4">
              <button onClick={() => setExpandedSub({ ...expandedSub, [g.id]: !expandedSub[g.id] })} className="w-full flex items-center justify-between mb-2" data-testid={`grouped-tec-toggle-${g.id}`}>
                <div className="flex items-center gap-2"><Users2 className="h-4 w-4 text-amber-600" /><h3 className="font-semibold">{g.nome}</h3></div>
                <Badge variant="outline">{g.tickets.length} ticket</Badge>
              </button>
              {expandedSub[g.id] !== false && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
                  {g.tickets.map((t, i) => <TicketCard key={t.id} t={t} i={i} isAdmin={isAdmin} onOpen={() => setSelected(t.id)} onEdit={() => openEdit(t)} onArchive={() => archive(t.id)} onRestore={() => restore(t.id)} onDelete={() => del(t.id)} onGoto={() => t.group_id && t.cantiere_id && t.riga_computo_id && navigate(`/g/${t.group_id}/cantieri/${t.cantiere_id}/righe/${t.riga_computo_id}?highlight=riga`)} archived={filters.is_archived} />)}
                </div>
              )}
            </CardContent></Card>
          ))}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {rows.map((t, i) => <TicketCard key={t.id} t={t} i={i} isAdmin={isAdmin} onOpen={() => setSelected(t.id)} onEdit={() => openEdit(t)} onArchive={() => archive(t.id)} onRestore={() => restore(t.id)} onDelete={() => del(t.id)} onGoto={() => t.group_id && t.cantiere_id && t.riga_computo_id && navigate(`/g/${t.group_id}/cantieri/${t.cantiere_id}/righe/${t.riga_computo_id}?highlight=riga`)} archived={filters.is_archived} />)}
          </div>
        )
      )}

      {selected && <TicketDialog id={selected} onClose={() => { setSelected(null); load(); }} />}

      <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); setEditing(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica" : "Nuovo"} ticket {isSub ? "subappaltatore" : "tecnico"}</DialogTitle>
            <DialogDescription>{isSub ? "Il ticket subappaltatore è autonomo e informativo. Non genera SAL o ordini." : "Ticket collegato al cantiere selezionato."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            {isSub && (
              <div className="flex gap-2 items-center">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Tipo ticket:</div>
                <div className="flex gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 p-0.5 w-fit">
                  <button type="button" onClick={() => setForm({ ...form, subtipo: "manodopera" })} data-testid="form-subtipo-dipendenti" className={`px-3 py-1 text-xs font-semibold rounded ${form.subtipo === "manodopera" ? "bg-white dark:bg-slate-900 text-amber-600 shadow-sm" : "text-slate-500"}`}>Dipendenti</button>
                  <button type="button" onClick={() => setForm({ ...form, subtipo: "materiali" })} data-testid="form-subtipo-materiali" className={`px-3 py-1 text-xs font-semibold rounded ${form.subtipo === "materiali" ? "bg-white dark:bg-slate-900 text-amber-600 shadow-sm" : "text-slate-500"}`}>Materiali</button>
                </div>
              </div>
            )}
            {isTec && (
              <div className="flex gap-2 items-center">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Tipo ticket:</div>
                <div className="flex gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 p-0.5 w-fit">
                  <button type="button" onClick={() => setForm({ ...form, subtipo: "manodopera" })} data-testid="form-subtipo-dipendenti-tec" className={`px-3 py-1 text-xs font-semibold rounded ${form.subtipo === "manodopera" ? "bg-white dark:bg-slate-900 text-amber-600 shadow-sm" : "text-slate-500"}`}>Dipendenti</button>
                  <button type="button" onClick={() => setForm({ ...form, subtipo: "materiali" })} data-testid="form-subtipo-materiali-tec" className={`px-3 py-1 text-xs font-semibold rounded ${form.subtipo === "materiali" ? "bg-white dark:bg-slate-900 text-amber-600 shadow-sm" : "text-slate-500"}`}>Materiali</button>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Cantiere *</Label><Select value={form.cantiere_id} onValueChange={(v) => setForm({ ...form, cantiere_id: v })}><SelectTrigger data-testid="form-cantiere"><SelectValue placeholder="Seleziona" /></SelectTrigger><SelectContent>{cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.code} · {c.nome}</SelectItem>)}</SelectContent></Select></div>
              {isSub && !isSubUser && (
                <div><Label>Subappaltatore *</Label>
                  <Select value={form.subappaltatore_id} onValueChange={(v) => setForm({ ...form, subappaltatore_id: v, dipendente_id: "none" })}>
                    <SelectTrigger data-testid="form-sub"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>{assignees.map(a => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {isSub && isSubUser && (
                <div><Label>Subappaltatore</Label>
                  <div className="h-10 px-3 flex items-center rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 text-sm font-mono" data-testid="form-sub-locked">
                    la tua ditta (bloccato)
                  </div>
                </div>
              )}
            </div>
            {isSub && form.subappaltatore_id !== "none" && !editing && (
              <div><Label className="text-[10px]">Dipendente collegamento singolo (opzionale, legacy)</Label>
                <Select value={form.dipendente_id} onValueChange={(v) => setForm({ ...form, dipendente_id: v })}>
                  <SelectTrigger data-testid="form-dip"><SelectValue placeholder="— nessuno —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— nessuno —</SelectItem>
                    {dips.map(d => <SelectItem key={d.id} value={d.id}>{d.cognome} {d.nome}{d.ruolo ? ` · ${d.ruolo}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isSub && (
              <div><Label>Data</Label><Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="form-data" /></div>
            )}
            {(isSub || isTec) && form.subtipo === "manodopera" && !editing && (
              <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded border border-amber-200 dark:border-amber-900 space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs uppercase tracking-widest text-amber-700 font-semibold">Dipendenti impiegati</div>
                  <Button type="button" size="sm" variant="outline" onClick={addLine} disabled={isSub && form.subappaltatore_id === "none"} data-testid="form-add-line"><Plus className="h-3 w-3 mr-1" /> Aggiungi dipendente</Button>
                </div>
                {isSub && form.subappaltatore_id === "none" ? (
                  <p className="text-xs text-slate-500 text-center py-2">Seleziona prima un subappaltatore.</p>
                ) : (isSub && dips.length === 0) ? (
                  <p className="text-xs text-slate-500 text-center py-2">Nessun dipendente attivo per questo subappaltatore.</p>
                ) : (
                  <div className="space-y-2">
                    {(form.lines || []).map((l, i) => (
                        <div key={l._key || i} className="grid grid-cols-12 gap-2 items-end" data-testid={`form-line-${i}`}>
                          <div className="col-span-5"><Label className="text-[10px]">Dipendente *</Label>
                            <Select value={l.dipendente_id} onValueChange={(v) => onPickLineDip(i, v)}>
                              <SelectTrigger className="h-8 text-xs" data-testid={`form-line-dip-${i}`}><SelectValue placeholder="Scegli…" /></SelectTrigger>
                              <SelectContent>{dips.filter(d => d.id === l.dipendente_id || !usedLineDipIds.has(d.id)).map(d => (
                                <SelectItem key={d.id} value={d.id}>{d.cognome} {d.nome}{d.ruolo ? ` · ${d.ruolo}` : ""}</SelectItem>
                              ))}</SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-4"><Label className="text-[10px]">Tipo operaio</Label>
                            <Input className="h-8 text-xs" value={l.tipo_operaio} onChange={(e) => updateLine(i, { tipo_operaio: e.target.value })} placeholder="Es. Muratore" data-testid={`form-line-tipo-${i}`} />
                          </div>
                          <div className="col-span-2"><Label className="text-[10px]">Ore *</Label>
                            <Input className="h-8 text-xs" type="number" step="0.5" min="0" value={l.ore} onChange={(e) => updateLine(i, { ore: e.target.value })} data-testid={`form-line-ore-${i}`} />
                          </div>
                          <div className="col-span-1 flex items-center justify-end">
                            <Button type="button" size="icon" variant="ghost" onClick={() => removeLine(i)} disabled={form.lines.length === 1} data-testid={`form-line-del-${i}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                          </div>
                        </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {(isSub || isTec) && form.subtipo === "materiali" && !editing && (
              <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded border border-amber-200 dark:border-amber-900 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-widest text-amber-700 font-semibold">Materiali (opzionale)</div>
                  <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, materiali_lines: [...(form.materiali_lines || []), emptyMatLine()] })} data-testid="form-mat-add"><Plus className="h-3 w-3 mr-1" /> Aggiungi materiale</Button>
                </div>
                {(form.materiali_lines || []).map((m, i) => (
                  <div key={m._key || i} className="grid grid-cols-12 gap-2 items-end" data-testid={`form-mat-row-${i}`}>
                    <div className="col-span-3"><Label className="text-[10px]">Tipo lavorazione</Label><Input className="h-8 text-xs" value={m.tipo_lavorazione} onChange={(e) => setForm({ ...form, materiali_lines: form.materiali_lines.map((r, idx) => idx === i ? { ...r, tipo_lavorazione: e.target.value } : r) })} /></div>
                    <div className="col-span-4"><Label className="text-[10px]">Nome materiale *</Label><Input className="h-8 text-xs" value={m.materiale_nome} onChange={(e) => setForm({ ...form, materiali_lines: form.materiali_lines.map((r, idx) => idx === i ? { ...r, materiale_nome: e.target.value } : r) })} /></div>
                    <div className="col-span-2"><Label className="text-[10px]">Quantità</Label><Input className="h-8 text-xs" type="number" step="0.0001" value={m.quantita} onChange={(e) => setForm({ ...form, materiali_lines: form.materiali_lines.map((r, idx) => idx === i ? { ...r, quantita: e.target.value } : r) })} /></div>
                    <div className="col-span-2"><Label className="text-[10px]">UM</Label><Input className="h-8 text-xs" value={m.unita_misura} onChange={(e) => setForm({ ...form, materiali_lines: form.materiali_lines.map((r, idx) => idx === i ? { ...r, unita_misura: e.target.value } : r) })} /></div>
                    <div className="col-span-1"><Button type="button" size="icon" variant="ghost" onClick={() => setForm({ ...form, materiali_lines: form.materiali_lines.length > 1 ? form.materiali_lines.filter((_, idx) => idx !== i) : form.materiali_lines })}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button></div>
                  </div>
                ))}
              </div>
            )}
            {isSub && editing && (
              <p className="text-xs text-slate-500 p-3 bg-slate-50 dark:bg-slate-900/40 rounded border">Righe manodopera/materiali del ticket: append-only. Usa il dettaglio del ticket per aggiungere nuove righe (non è possibile modificare o cancellare le esistenti).</p>
            )}
            <div><Label>Titolo *</Label><Input required value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} data-testid="form-titolo" /></div>
            <div><Label>Descrizione / Note</Label><Textarea value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} data-testid="form-descr" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Priorità</Label><Select value={form.priorita} onValueChange={(v) => setForm({ ...form, priorita: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PRIORITA.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Scadenza</Label><Input type="date" value={form.scadenza} onChange={(e) => setForm({ ...form, scadenza: e.target.value })} /></div>
            </div>
            <DialogFooter><Button type="submit" data-testid="form-submit" className="bg-amber-500 text-slate-950 font-semibold">{editing ? "Salva modifiche" : "Crea"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TicketCard({ t, i, isAdmin, onOpen, onEdit, onArchive, onRestore, onDelete, onGoto, archived }) {
  const canGoto = !!(t.group_id && t.cantiere_id && t.riga_computo_id);
  // Nel tab "storico cantiere" (rows misti) usa lo stato reale per-item.
  const rowArchived = !!t.is_archived;
  const showEdit = isAdmin && !rowArchived;
  const showArchive = isAdmin && !rowArchived;
  const showRestore = isAdmin && rowArchived;
  return (
    <div className={`text-left p-4 border-l-4 rounded-r-lg shadow ${POST_COLORS[i % POST_COLORS.length]} ${rowArchived ? "opacity-70" : ""}`} data-testid={`ticket-postit-${t.id}`}>
      <div className="flex items-start justify-between mb-1">
        <span className="text-xs font-mono text-slate-500">#{t.numero}{t.subtipo && ` · ${t.subtipo === "manodopera" ? "Dipendenti" : "Materiali"}`}</span>
        <div className="flex items-center gap-1">
          {rowArchived && <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-slate-500 text-white" data-testid={`ticket-badge-arch-${t.id}`}>ARCHIVIATO</span>}
          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${PRIO_COLOR[t.priorita]}`}>{t.priorita}</span>
        </div>
      </div>
      {(t.cantiere_nome || t.cantiere_code) && (
        <div className="text-[11px] text-slate-700 dark:text-slate-300 mb-1 truncate" data-testid={`ticket-cantiere-${t.id}`}>
          <span className="font-semibold">Cantiere:</span> {t.cantiere_code ? <span className="font-mono">{t.cantiere_code}</span> : null}{t.cantiere_code && t.cantiere_nome ? " · " : ""}{t.cantiere_nome || ""}
        </div>
      )}
      <button onClick={onOpen} className="text-left w-full cursor-pointer" data-testid={`ticket-open-${t.id}`}><h3 className="font-bold text-sm mb-1 text-slate-900">{t.titolo}</h3></button>
      {Array.isArray(t.lines) && t.lines.length > 0 ? (
        <div className="text-xs text-amber-700 font-semibold mb-1">
          {t.lines.length} dipendent{t.lines.length === 1 ? "e" : "i"}
          <div className="text-[10px] text-slate-600 font-normal mt-0.5 space-y-0.5">
            {t.lines.slice(0, 3).map(l => (
              <div key={l.id}>· <b>{l.dipendente_nome}</b>{l.tipo_operaio ? ` (${l.tipo_operaio})` : ""}{l.ore != null ? ` — ${fmt(l.ore)}h` : ""}</div>
            ))}
            {t.lines.length > 3 && <div className="italic">+{t.lines.length - 3} altr{t.lines.length - 3 === 1 ? "o" : "i"}…</div>}
          </div>
        </div>
      ) : t.subtipo === "manodopera" && t.ore != null && (
        <div className="text-xs text-amber-700 font-semibold mb-1">{fmt(t.ore)}h{t.num_operai ? ` · ${t.num_operai} operai` : ""}</div>
      )}
      {Array.isArray(t.materiali_lines) && t.materiali_lines.length > 0 ? (
        <div className="text-xs text-slate-700 mb-1">
          <b>{t.materiali_lines.length} material{t.materiali_lines.length === 1 ? "e" : "i"}</b>
          {t.materiali_lines.slice(0, 3).map(m => (
            <div key={m.id} className="text-[10px] text-slate-600">· {m.materiale_nome}{m.quantita != null ? ` — ${fmt(m.quantita)} ${m.unita_misura || ""}` : ""}</div>
          ))}
          {t.materiali_lines.length > 3 && <div className="text-[10px] italic">+{t.materiali_lines.length - 3} altri…</div>}
        </div>
      ) : t.subtipo === "materiali" && (t.materiale_nome || t.quantita) && <div className="text-xs text-slate-700 mb-1">{t.materiale_nome} — {fmt(t.quantita)} {t.unita_misura}</div>}
      {t.descrizione && <p className="text-xs text-slate-600 mb-2 line-clamp-2">{t.descrizione}</p>}
      <div className="flex items-center justify-between text-[10px] text-slate-500">
        <span className={`px-2 py-0.5 rounded ${STATO_COLOR[t.stato]}`}>{String(t.stato).replace("_"," ")}</span>
        {t.data && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {t.data}</span>}
      </div>
      <div className="flex gap-1 mt-2 justify-end">
        {canGoto && <Button size="icon" variant="ghost" onClick={onGoto} title="Vai alla riga computo" data-testid={`ticket-goto-${t.id}`}><ExternalLink className="h-3.5 w-3.5" /></Button>}
        {showEdit && <Button size="icon" variant="ghost" onClick={onEdit} title="Modifica" data-testid={`ticket-edit-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
        {showArchive && <Button size="icon" variant="ghost" onClick={onArchive} title="Archivia" data-testid={`ticket-archive-${t.id}`}><Archive className="h-3.5 w-3.5" /></Button>}
        {showRestore && <Button size="icon" variant="ghost" onClick={onRestore} title="Ripristina" data-testid={`ticket-restore-${t.id}`}><ArchiveRestore className="h-3.5 w-3.5" /></Button>}
        {isAdmin && <Button size="icon" variant="ghost" onClick={onDelete} title="Elimina definitivamente" data-testid={`ticket-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>}
      </div>
    </div>
  );
}

function TicketDialog({ id, onClose }) {
  const [t, setT] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [loadErr, setLoadErr] = useState(null);
  const { user } = useAuth();
  const canEdit = user && ["ADMIN","TECNICO"].includes(user.role);
  const load = async () => {
    try {
      const ticket = await api.get(`/tickets/${id}`);
      setT(ticket.data);
      setLoadErr(null);
    } catch (e) {
      setLoadErr(e.response?.data?.detail || e.message || "Errore caricamento ticket");
      return;
    }
    try {
      const messages = await api.get(`/tickets/${id}/messages`);
      setMsgs(messages.data || []);
    } catch (e) {
      setMsgs([]); // messages optional: non blocca il dettaglio
    }
  };
  useEffect(() => { load(); }, [id]);
  const send = async () => { if (!text.trim()) return; try { await api.post(`/tickets/${id}/messages`, { messaggio: text }); setText(""); load(); } catch(e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const patch = async (updates) => { try { await api.patch(`/tickets/${id}`, updates); load(); } catch(e) { toast.error(e.response?.data?.detail || "Errore"); } };
  return (
    <Dialog open={true} onOpenChange={onClose}><DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
      <DialogHeader><DialogTitle>{t ? `#${t.numero} · ${t.titolo}` : (loadErr ? "Errore caricamento" : "Caricamento…")}</DialogTitle>
        {t && <DialogDescription>{t.descrizione || "Nessuna descrizione"}</DialogDescription>}
        {!t && loadErr && <DialogDescription className="text-rose-600" data-testid="ticket-load-err">{loadErr}</DialogDescription>}
      </DialogHeader>
      {t && (
        <div className="flex-1 overflow-hidden flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">{t.tipo}{t.subtipo && ` · ${t.subtipo}`}</Badge>
            {canEdit ? <Select value={t.stato} onValueChange={(v) => patch({ stato: v })}><SelectTrigger className="h-7 w-40 text-xs" data-testid="ticket-stato-select"><SelectValue /></SelectTrigger><SelectContent>{STATI.map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent></Select> : <Badge>{t.stato}</Badge>}
            {canEdit ? <Select value={t.priorita} onValueChange={(v) => patch({ priorita: v })}><SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger><SelectContent>{PRIORITA.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select> : <Badge>{t.priorita}</Badge>}
            {t.data && <Badge variant="outline">Data {t.data}</Badge>}
            {t.scadenza && <Badge variant="outline">Scad. {t.scadenza}</Badge>}
            {t.subtipo === "manodopera" && Number(t.costo_totale || 0) > 0 && <Badge className="bg-amber-500 text-slate-950">Totale {fmtEuro(t.costo_totale)}</Badge>}
          </div>
          {Array.isArray(t.lines) && t.lines.length > 0 && (
            <div className="border border-amber-200 dark:border-amber-900 rounded-md p-3 bg-amber-50/50 dark:bg-amber-950/10" data-testid="ticket-lines">
              <div className="text-xs uppercase tracking-widest text-amber-700 font-semibold mb-2">Dipendenti impiegati ({t.lines.length})</div>
              <table className="w-full text-xs">
                <thead><tr className="text-left text-slate-500"><th className="pb-1">Dipendente</th><th>Tipo</th><th className="text-right">Ore</th>{t.lines.some(l => Number(l.costo_orario || 0) > 0) && <><th className="text-right">€/h</th><th className="text-right">Totale</th></>}</tr></thead>
                <tbody>{t.lines.map(l => (
                  <tr key={l.id} className="border-t border-amber-100 dark:border-amber-900" data-testid={`ticket-line-${l.id}`}>
                    <td className="py-1"><b>{l.dipendente_nome}</b>{l.dipendente_is_archived && <Badge className="ml-1 bg-slate-500 text-white text-[9px]">arch.</Badge>}</td>
                    <td>{l.tipo_operaio || "—"}</td>
                    <td className="text-right font-mono">{l.ore != null ? fmt(l.ore) : "—"}</td>
                    {t.lines.some(x => Number(x.costo_orario || 0) > 0) && <>
                      <td className="text-right font-mono">{Number(l.costo_orario || 0) > 0 ? fmtEuro(l.costo_orario) : "—"}</td>
                      <td className="text-right font-mono font-semibold">{Number(l.costo_totale || 0) > 0 ? fmtEuro(l.costo_totale) : "—"}</td>
                    </>}
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
          {(Array.isArray(t.materiali_lines) && t.materiali_lines.length > 0 || (t.subtipo === "materiali" && t.materiale_nome)) && (
            <div className="border border-amber-200 dark:border-amber-900 rounded-md p-3 bg-amber-50/50 dark:bg-amber-950/10" data-testid="ticket-materiali">
              <div className="text-xs uppercase tracking-widest text-amber-700 font-semibold mb-2">Materiali</div>
              <table className="w-full text-xs">
                <thead><tr className="text-left text-slate-500"><th className="pb-1">Tipo lavorazione</th><th>Materiale</th><th className="text-right">Quantità</th><th>UM</th></tr></thead>
                <tbody>
                  {(t.materiali_lines || []).map(m => (
                    <tr key={m.id} className="border-t border-amber-100 dark:border-amber-900" data-testid={`ticket-mat-${m.id}`}>
                      <td className="py-1">{m.tipo_lavorazione || "—"}</td>
                      <td><b>{m.materiale_nome}</b></td>
                      <td className="text-right font-mono">{m.quantita != null ? fmt(m.quantita) : "—"}</td>
                      <td>{m.unita_misura || "—"}</td>
                    </tr>
                  ))}
                  {!(t.materiali_lines || []).length && t.materiale_nome && (
                    <tr className="border-t"><td>{t.tipo_lavorazione || "—"}</td><td><b>{t.materiale_nome}</b> <span className="text-[9px] text-slate-500">(legacy)</span></td><td className="text-right font-mono">{fmt(t.quantita)}</td><td>{t.unita_misura || "—"}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {t.tipo === "subappaltatore" && !t.is_archived && (
            <AppendLinesPanel ticket={t} onChange={load} />
          )}
          <div className="flex-1 overflow-y-auto space-y-3 border-t border-b border-slate-200 dark:border-slate-800 py-3">
            {msgs.length === 0 ? <p className="text-slate-500 text-sm text-center py-4">Nessun messaggio. Scrivi il primo.</p> : msgs.map(m => (
              <div key={m.id} className="flex gap-3" data-testid={`msg-${m.id}`}>
                <div className="w-9 h-9 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center text-xs font-bold shrink-0">{(m.author_name || "?").split(" ").map(s => s[0]).slice(0,2).join("")}</div>
                <div className="flex-1"><div className="flex items-baseline gap-2"><b className="text-sm">{m.author_name}</b>{m.author_role && <span className="text-[10px] uppercase tracking-widest text-slate-500">{m.author_role}</span>}<span className="text-xs text-slate-500 ml-auto">{new Date(m.created_at).toLocaleString("it-IT")}</span></div><div className="text-sm mt-1 whitespace-pre-wrap">{m.messaggio}</div></div>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Textarea placeholder="Scrivi un messaggio…" value={text} onChange={(e) => setText(e.target.value)} rows={2} data-testid="ticket-msg-input" />
            <Button onClick={send} disabled={!text.trim()} data-testid="ticket-msg-send" className="bg-amber-500 text-slate-950 font-semibold self-end"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </DialogContent></Dialog>
  );
}


function KanbanView({ rows, canDrag, onStatoChange, cardProps }) {
  const COLS = [
    { key: "aperto", label: "Aperto", color: "border-emerald-300 bg-emerald-50/50 dark:bg-emerald-950/10" },
    { key: "in_lavorazione", label: "In lavorazione", color: "border-blue-300 bg-blue-50/50 dark:bg-blue-950/10" },
    { key: "risolto", label: "Risolto", color: "border-slate-300 bg-slate-50/70 dark:bg-slate-800/40" },
  ];
  const [dragOver, setDragOver] = useState(null);
  const onDrop = (e, stato) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("text/ticket-id");
    if (id) onStatoChange(id, stato);
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="kanban-board">
      {COLS.map(col => {
        const items = rows.filter(r => r.stato === col.key);
        const over = dragOver === col.key;
        return (
          <div
            key={col.key}
            data-testid={`kanban-col-${col.key}`}
            onDragOver={(e) => { if (canDrag) { e.preventDefault(); setDragOver(col.key); } }}
            onDragLeave={() => setDragOver(prev => prev === col.key ? null : prev)}
            onDrop={(e) => canDrag && onDrop(e, col.key)}
            className={`rounded-lg border-2 border-dashed p-2 min-h-[300px] ${col.color} ${over ? "ring-2 ring-amber-400" : ""}`}
          >
            <div className="flex items-center justify-between mb-2 px-1">
              <h3 className="text-xs font-bold uppercase tracking-widest">{col.label}</h3>
              <Badge variant="outline" className="text-[10px]">{items.length}</Badge>
            </div>
            <div className="space-y-2">
              {items.map((t, i) => (
                <div
                  key={t.id}
                  draggable={canDrag}
                  onDragStart={(e) => { e.dataTransfer.setData("text/ticket-id", t.id); e.dataTransfer.effectAllowed = "move"; }}
                  data-testid={`kanban-card-${t.id}`}
                  className={canDrag ? "cursor-grab active:cursor-grabbing" : ""}
                >
                  <TicketCard
                    t={t} i={i}
                    isAdmin={cardProps.isAdmin} archived={cardProps.archived}
                    onOpen={() => cardProps.onOpen(t)}
                    onEdit={() => cardProps.onEdit(t)}
                    onArchive={() => cardProps.onArchive(t)}
                    onRestore={() => cardProps.onRestore(t)}
                    onDelete={() => cardProps.onDelete(t)}
                    onGoto={() => cardProps.onGoto(t)}
                  />
                </div>
              ))}
              {items.length === 0 && <div className="text-[11px] text-slate-400 italic text-center py-4">Trascina qui</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AppendLinesPanel({ ticket, onChange }) {
  const [addMat, setAddMat] = useState(false);
  const [addMano, setAddMano] = useState(false);
  const [mat, setMat] = useState({ tipo_lavorazione: "", materiale_nome: "", quantita: "", unita_misura: "" });
  const [dips, setDips] = useState([]);
  const [mano, setMano] = useState({ dipendente_id: "", tipo_operaio: "", ore: "8" });
  useEffect(() => {
    if (addMano && ticket.subappaltatore_id) {
      api.get("/dipendenti-subappaltatori", { params: { subappaltatore_id: ticket.subappaltatore_id } })
        .then(r => setDips(r.data || [])).catch(() => setDips([]));
    }
  }, [addMano, ticket.subappaltatore_id]);
  const saveMat = async () => {
    if (!mat.materiale_nome.trim()) return toast.error("Nome materiale obbligatorio");
    try {
      await api.post(`/tickets/${ticket.id}/lines/materiali`, {
        tipo_lavorazione: mat.tipo_lavorazione || null, materiale_nome: mat.materiale_nome.trim(),
        quantita: mat.quantita !== "" ? Number(mat.quantita) : null,
        unita_misura: mat.unita_misura || null,
      });
      setMat({ tipo_lavorazione: "", materiale_nome: "", quantita: "", unita_misura: "" });
      setAddMat(false); onChange(); toast.success("Materiale aggiunto");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const saveMano = async () => {
    if (!mano.dipendente_id) return toast.error("Seleziona un dipendente");
    if (!(Number(mano.ore) > 0)) return toast.error("Ore obbligatorie");
    try {
      await api.post(`/tickets/${ticket.id}/lines/manodopera`, {
        dipendente_id: mano.dipendente_id, tipo_operaio: mano.tipo_operaio || null,
        ore: Number(mano.ore), costo_orario: null,
      });
      setMano({ dipendente_id: "", tipo_operaio: "", ore: "8" });
      setAddMano(false); onChange(); toast.success("Dipendente aggiunto");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="border border-dashed border-slate-300 rounded-md p-2 flex flex-wrap gap-2 items-center" data-testid="append-panel">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Aggiungi (append-only)</span>
      {!addMat && !addMano && (
        <>
          <Button size="sm" variant="outline" onClick={() => setAddMano(true)} data-testid="append-mano-btn"><Plus className="h-3 w-3 mr-1" /> Dipendente</Button>
          <Button size="sm" variant="outline" onClick={() => setAddMat(true)} data-testid="append-mat-btn"><Plus className="h-3 w-3 mr-1" /> Materiale</Button>
        </>
      )}
      {addMano && (
        <div className="w-full grid grid-cols-12 gap-2 items-end">
          <div className="col-span-4"><Label className="text-[10px]">Dipendente *</Label>
            <Select value={mano.dipendente_id} onValueChange={(v) => { const d = dips.find(x => x.id === v); setMano({ ...mano, dipendente_id: v, tipo_operaio: d?.ruolo || mano.tipo_operaio }); }}>
              <SelectTrigger className="h-8 text-xs" data-testid="append-mano-dip"><SelectValue placeholder="Scegli…" /></SelectTrigger>
              <SelectContent>{dips.map(d => <SelectItem key={d.id} value={d.id}>{d.cognome} {d.nome}{d.ruolo ? ` · ${d.ruolo}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-3"><Label className="text-[10px]">Tipo</Label><Input className="h-8 text-xs" value={mano.tipo_operaio} onChange={(e) => setMano({ ...mano, tipo_operaio: e.target.value })} /></div>
          <div className="col-span-4"><Label className="text-[10px]">Ore *</Label><Input className="h-8 text-xs" type="number" step="0.5" value={mano.ore} onChange={(e) => setMano({ ...mano, ore: e.target.value })} /></div>
          <div className="col-span-1"><Button size="sm" className="h-8 bg-amber-500 text-slate-950" onClick={saveMano} data-testid="append-mano-save">OK</Button></div>
          <div className="col-span-12"><Button size="sm" variant="ghost" onClick={() => setAddMano(false)}>Annulla</Button></div>
        </div>
      )}
      {addMat && (
        <div className="w-full grid grid-cols-12 gap-2 items-end">
          <div className="col-span-3"><Label className="text-[10px]">Tipo lavorazione</Label><Input className="h-8 text-xs" value={mat.tipo_lavorazione} onChange={(e) => setMat({ ...mat, tipo_lavorazione: e.target.value })} /></div>
          <div className="col-span-4"><Label className="text-[10px]">Materiale *</Label><Input className="h-8 text-xs" value={mat.materiale_nome} onChange={(e) => setMat({ ...mat, materiale_nome: e.target.value })} data-testid="append-mat-nome" /></div>
          <div className="col-span-2"><Label className="text-[10px]">Quantità</Label><Input className="h-8 text-xs" type="number" step="0.0001" value={mat.quantita} onChange={(e) => setMat({ ...mat, quantita: e.target.value })} /></div>
          <div className="col-span-2"><Label className="text-[10px]">UM</Label><Input className="h-8 text-xs" value={mat.unita_misura} onChange={(e) => setMat({ ...mat, unita_misura: e.target.value })} /></div>
          <div className="col-span-1"><Button size="sm" className="h-8 bg-amber-500 text-slate-950" onClick={saveMat} data-testid="append-mat-save">OK</Button></div>
          <div className="col-span-12"><Button size="sm" variant="ghost" onClick={() => setAddMat(false)}>Annulla</Button></div>
        </div>
      )}
    </div>
  );
}

